import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Search, Loader2, Phone, ExternalLink, ShieldQuestion } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePhoneLookup } from "@/hooks/usePhoneLookup";

export const Route = createFileRoute("/admin/phone-lookup")({
  component: PhoneLookupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    phone: typeof search.phone === "string" ? search.phone : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Phone Lookup · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function PhoneLookupPage() {
  const { dir } = useLocale();
  const isAr = dir === "rtl";
  const { phone: initialPhone } = Route.useSearch();
  const [input, setInput] = React.useState(initialPhone ?? "");
  const [submitted, setSubmitted] = React.useState(initialPhone ?? "");
  const { data, isFetching } = usePhoneLookup(submitted, submitted.length >= 3);

  React.useEffect(() => {
    if (initialPhone && initialPhone !== submitted) {
      setInput(initialPhone);
      setSubmitted(initialPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhone]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(input.trim());
  };


  return (
    <SiteShell>
      <AdminNav />
      <main className="container mx-auto max-w-3xl px-4 py-6 space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldQuestion className="h-6 w-6" />
            {isAr ? "كاشف الرقم — هذا الرقم لِمن؟" : "Phone Lookup — Who is this number?"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? "أدخل أيّ رقم بأيّ صيغة (+971 أو 0050 أو 501234567). يبحث في العملاء، السفراء، الفريق، السائقين، طلبات الانضمام، موافقات واتساب، وطلبات Shopify."
              : "Enter any number in any format. Searches customers, ambassadors, team, drivers, ambassador applications, WhatsApp opt-ins, and Shopify orders."}
          </p>
        </header>

        <form onSubmit={onSubmit} className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isAr ? "مثلاً 0501234567 أو +971501234567" : "e.g. 0501234567 or +971501234567"}
            dir="ltr"
            className="text-base"
          />
          <Button type="submit" disabled={input.trim().length < 3}>
            <Search className="h-4 w-4 me-1" />
            {isAr ? "بحث" : "Search"}
          </Button>
        </form>

        {isFetching && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isAr ? "جارٍ البحث في كلّ المصادر…" : "Searching all sources…"}
          </div>
        )}

        {data && !isFetching && (
          <section className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {(() => {
                const grouped = groupMatches(data.matches);
                return isAr
                  ? `نتائج لـ «${data.query}» — ${grouped.length} شخص/سجلّ (${data.count} مطابقة)`
                  : `Results for "${data.query}" — ${grouped.length} person/record (${data.count} matches)`;
              })()}
            </div>

            {data.count === 0 ? (
              <div className="rounded-none border border-dashed p-6 text-center text-sm text-muted-foreground">
                {isAr
                  ? "لا يوجد هذا الرقم في أيّ مصدر داخل النظام. قد يكون زائراً جديداً راسلكم على واتساب فقط — راجع /admin/messages."
                  : "Number not found in any source. May be a new visitor who only messaged via WhatsApp — check /admin/messages."}
              </div>
            ) : (
              <ul className="space-y-2">
                {groupMatches(data.matches).map((group, i) => {
                  const primary = group[0];
                  return (
                    <li
                      key={i}
                      className="rounded-none border bg-card p-4 flex items-start justify-between gap-3"
                    >
                      <div className="space-y-2 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {group.map((m, j) => (
                            <React.Fragment key={j}>
                              <SourceBadge source={m.source} isAr={isAr} />
                              {(m.badge_ar || m.badge_en) && (
                                <Badge variant="secondary" className="text-xs">
                                  {isAr ? m.badge_ar : m.badge_en}
                                </Badge>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="font-medium truncate">
                          {primary.display_name || (isAr ? "بدون اسم" : "No name")}
                          {primary.user_id && (
                            <span className="ms-2 text-xs text-muted-foreground font-mono" dir="ltr">
                              #{primary.user_id.slice(0, 8)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1" dir="ltr">
                          <Phone className="h-3 w-3" />
                          {primary.matched_phone}
                        </div>
                        {group.map((m, j) =>
                          m.meta && Object.entries(m.meta).filter(([k, v]) => v && k !== "user_id_short").length > 0 ? (
                            <div key={`meta-${j}`} className="text-xs text-muted-foreground">
                              <span className="font-medium me-1">
                                {isAr ? m.label_ar : m.label_en}:
                              </span>
                              {Object.entries(m.meta)
                                .filter(([k, v]) => v && k !== "user_id_short")
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(" · ")}
                            </div>
                          ) : null,
                        )}
                        {group
                          .filter((m) => m.source === "guest_order" && m.order_details)
                          .map((m, j) => {
                            const od = m.order_details!;
                            const statusColor =
                              od.delivery_status === "delivered"
                                ? "bg-green-500/10 text-green-700 dark:text-green-300"
                                : od.delivery_status === "out_for_delivery"
                                ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
                                : od.delivery_status === "assigned"
                                ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
                                : od.delivery_status === "failed" || od.delivery_status === "returned"
                                ? "bg-red-500/10 text-red-700 dark:text-red-300"
                                : "bg-muted text-muted-foreground";
                            return (
                              <div key={`ord-${j}`} className="mt-2 rounded-none border bg-muted/30 p-2 text-xs space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-mono font-semibold">{od.order_name}</span>
                                  {od.delivery_status && (
                                    <Badge variant="secondary" className={`text-[10px] ${statusColor}`}>
                                      {od.delivery_status}
                                    </Badge>
                                  )}
                                  {od.market && (
                                    <span className="text-muted-foreground">{od.market}</span>
                                  )}
                                  {od.total != null && (
                                    <span className="text-muted-foreground" dir="ltr">
                                      {od.total} {od.currency ?? ""}
                                    </span>
                                  )}
                                </div>
                                {od.city && (
                                  <div className="text-muted-foreground">
                                    {isAr ? "المدينة:" : "City:"} {od.city}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">
                                    {isAr ? "السائق:" : "Driver:"}
                                  </span>
                                  {od.driver_name ? (
                                    <>
                                      <Link
                                        to={`/admin/drivers/${od.driver_id}` as string}
                                        className="text-primary hover:underline"
                                      >
                                        {od.driver_name}
                                      </Link>
                                      {od.driver_phone && (
                                        <span className="text-muted-foreground" dir="ltr">
                                          · {od.driver_phone}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-muted-foreground">
                                      {isAr ? "لم يُعيَّن بعد" : "Not assigned"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      <div className="flex flex-col gap-1">
                        {dedupeLinks(group).map((m, j) => (
                          <Button key={j} asChild size="sm" variant="outline">
                            <Link to={m.link!}>
                              {isAr ? `افتح ${m.label_ar}` : `Open ${m.label_en}`}
                              <ExternalLink className="h-3 w-3 ms-1" />
                            </Link>
                          </Button>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>
    </SiteShell>
  );
}

type MatchRow = NonNullable<ReturnType<typeof usePhoneLookup>["data"]>["matches"][number];

// يجمع كلّ المطابقات لنفس الشخص (نفس user_id) أو نفس الرقم الضيف في صفّ واحد
function groupMatches(matches: MatchRow[]): MatchRow[][] {
  const groups = new Map<string, MatchRow[]>();
  for (const m of matches) {
    const key = m.user_id ? `u:${m.user_id}` : `p:${m.matched_phone}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(m);
  }
  return Array.from(groups.values());
}

// يحذف الأزرار المكرّرة التي تؤدّي لنفس الرابط (عميل+سفير لنفس الشخص → زرّ واحد)
function dedupeLinks(group: MatchRow[]): MatchRow[] {
  const seen = new Set<string>();
  const out: MatchRow[] = [];
  for (const m of group) {
    if (!m.link || seen.has(m.link)) continue;
    seen.add(m.link);
    out.push(m);
  }
  return out;
}

function SourceBadge({
  source,
  isAr,
}: {
  source: string;
  isAr: boolean;
}) {
  const map: Record<string, { ar: string; en: string; cls: string }> = {
    customer: { ar: "عميل", en: "Customer", cls: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
    ambassador: { ar: "سفير", en: "Ambassador", cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300" },
    team: { ar: "فريق", en: "Team", cls: "bg-purple-500/10 text-purple-700 dark:text-purple-300" },
    driver: { ar: "سائق", en: "Driver", cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
    ambassador_application: { ar: "طلب سفير", en: "Application", cls: "bg-orange-500/10 text-orange-700 dark:text-orange-300" },
    whatsapp_opt_in: { ar: "واتساب", en: "WhatsApp", cls: "bg-green-500/10 text-green-700 dark:text-green-300" },
    guest_order: { ar: "طلب Shopify", en: "Shopify order", cls: "bg-pink-500/10 text-pink-700 dark:text-pink-300" },
  };
  const it = map[source] ?? { ar: source, en: source, cls: "bg-muted" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${it.cls}`}>
      {isAr ? it.ar : it.en}
    </span>
  );
}
